import { batchResponseType } from '../types/apiResponse/batchTypes'
import { useBatchStore, BatchState } from '../store/batch/useBatchStore'
import { useTankStore } from '../store/tank/useTankStore'
import { useSchoolStore } from '../store/school/useSchoolStore'
import { useGroupStore } from '../store/group/useGroupStore'

// ============================================
// Service Layer Imports (V2 API)
// ============================================
import { getGroups, getGroupsWithDetails, getReleasedGroups } from '../api/group/group.service'
import { getTanks, getEmptyTanks } from '../api/tank/tank.service'

import type { Group, GroupDetail, Tank } from '../types/domain'
import { schoolResponseType } from '../types/apiResponse/schoolTypes'

/**
 * Store batch data from Group details
 * V2 Migration: Batch concept replaced by Group
 * This function transforms Group data to legacy Batch store format for backward compatibility
 */
const storeBatchesFromGroups = (groupsWithDetails: GroupDetail[]) => {
  const batchesById: BatchState = {}

  if (Array.isArray(groupsWithDetails)) {
    groupsWithDetails.forEach((group) => {
      // Find the earliest start date among all schools in the group
      const items = group.items || []
      let earliestStartDate = 0

      items.forEach((item) => {
        if (item.school?.startDate) {
          const startDateNum = new Date(item.school.startDate).getTime()
          if (earliestStartDate === 0 || startDateNum < earliestStartDate) {
            earliestStartDate = startDateNum
          }
        }
      })

      // Map Group to Batch format (groupId = batchId, group.name = batchName)
      batchesById[group.id] = {
        batchName: group.name,
        startDate: earliestStartDate,
      }
    })
  }

  // Add special batches for ungrouped and empty tanks
  batchesById['$ungrouped'] = { batchName: 'Ungrouped Tanks', startDate: 0 }
  batchesById['$empty'] = { batchName: 'Empty Tanks', startDate: 0 }

  useBatchStore.getState().setBatches(batchesById)
}

/**
 * Store tanks from Domain Tank array
 * @param tanks - Domain Tank array from service layer
 */
const storeTanks = (tanks: Tank[]) => {
  // Convert array to Record<string, Tank> for store
  const tankStoreState: Record<string, Tank> = {}
  tanks.forEach((tank) => {
    tankStoreState[tank.id] = tank
  })

  useTankStore.getState().setTanks(tankStoreState)
}

export const addSchool = (schoolResponse: schoolResponseType) => {
  const { schoolId, ...schoolInfo } = schoolResponse
  useSchoolStore.getState().addSchool(schoolId, schoolInfo)
}

export const delSchool = (schoolId: string) => {
  useSchoolStore.getState().delSchool(schoolId)
}

export const updateSchool = (schoolResponse: schoolResponseType) => {
  const { schoolId, ...schoolInfo } = schoolResponse
  useSchoolStore.getState().updateSchool(schoolId, schoolInfo)
}

export const delSchoolStoreMany = (schoolIdList: string[]) => {
  // Defensive check: ensure schoolIdList is an array before iterating
  if (Array.isArray(schoolIdList)) {
    schoolIdList.forEach((schoolId) => delSchool(schoolId))
  }
}

/**
 * Reload school data from V2 Group API
 * V2 Migration: School data is now embedded in Group responses
 * This function reloads all group data to refresh school information
 *
 * @param _schoolIdList - Ignored in V2 (kept for backward compatibility)
 */
export const reloadSchool = async (_schoolIdList?: string[]) => {
  try {
    const groupsWithDetails = await getGroupsWithDetails()
    storeGroupsWithDetails(groupsWithDetails)
  } catch (error) {
    console.error('Failed to reload school:', error)
  }
}

export const addBatch = (batchResponse: batchResponseType) => {
  const { batchId, ...batchInfo } = batchResponse
  useBatchStore.getState().addBatch(batchId, batchInfo)
}

export const delBatch = (batchId: string) => {
  useBatchStore.getState().delBatch(batchId)
}

export const updateBatch = (batchResponse: batchResponseType) => {
  const { batchId, ...batchInfo } = batchResponse
  useBatchStore.getState().updateBatch(batchId, batchInfo)
}

/**
 * Reload batches from V2 Group API
 * V2 Migration: Uses getGroupsWithDetails() and transforms to legacy Batch format
 */
export const reloadBatches = async () => {
  try {
    const groupsWithDetails = await getGroupsWithDetails()
    storeBatchesFromGroups(groupsWithDetails)
  } catch (error) {
    console.error('Failed to reload batches:', error)
  }
}

/**
 * Reload tanks from service layer
 * Uses new async/await pattern with getTanks() service
 */
export const reloadTanks = async () => {
  try {
    const tanks = await getTanks()
    storeTanks(tanks)
  } catch (error) {
    console.error('Failed to reload tanks:', error)
  }
}

/**
 * Store groups from Domain Group array
 * @param groups - Domain Group array from service layer
 */
const storeGroups = (groups: Group[]) => {
  useGroupStore.getState().setGroups(groups)
}

/**
 * Reload groups from service layer
 * Uses new async/await pattern with getGroups() service
 */
export const reloadGroups = async () => {
  try {
    const groups = await getGroups()
    storeGroups(groups)
  } catch (error) {
    console.error('Failed to reload groups:', error)
  }
}

const storeGroupsWithDetails = (groupsWithDetails: GroupDetail[]) => {
  useGroupStore.getState().setGroupsWithDetails(groupsWithDetails)

  // Extract and store school data from groups
  // This allows SchoolCard to work with the existing store pattern
  if (Array.isArray(groupsWithDetails)) {
    groupsWithDetails.forEach((group) => {
      const items = group.items || []

      items.forEach((item) => {
        if (item.school && item.tank) {
          const school = item.school
          const tank = item.tank

          // Use domain model properties (already converted by mapper)
          // Use ?? null to preserve null for missing data (will display as '---' in UI)
          const averageWeight = school.averageWeight ?? school.weightPerFish ?? null
          const count = school.count ?? null
          const totalBiomass = school.totalBiomass ?? (
            count != null && averageWeight != null ? count * averageWeight : null
          )
          const density = school.density ?? (
            tank.volume > 0 && totalBiomass != null ? totalBiomass / tank.volume : null
          )

          // Convert startDate to moveDate format (YYYYMMDD)
          let moveDate: number | null = null
          if (school.startDate) {
            const date = new Date(school.startDate)
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            moveDate = parseInt(`${year}${month}${day}`)
          }

          // Store school data
          useSchoolStore.getState().addSchool(school.id, {
            averageWeight,
            count,
            totalBiomass,
            density,
            feedingRate: school.feedingRate ?? null,
            moveDate,
            startDate: school.startDate,
          })
        }
      })
    })
  }
}

/**
 * Reload groups with school and tank details from service layer
 * Uses new async/await pattern with getGroupsWithDetails() service
 * V2 Optimization: Uses /tank/empty and /group/released instead of individual getTankById calls
 */
export const reloadGroupsWithSchoolsAndTanks = async () => {
  try {
    // 1. 관리 그룹 조회 (GET /group/assigned)
    const groupsWithDetails = await getGroupsWithDetails()
    storeGroupsWithDetails(groupsWithDetails)

    // 2. 빈 탱크 조회 (GET /tank/empty)
    const emptyTanks = await getEmptyTanks()
    useTankStore.getState().setEmptyTanks(emptyTanks)

    // 3. 비관리 그룹 조회 (GET /group/released) - Ungrouped tanks with fish
    const releasedGroups = await getReleasedGroups()
    useGroupStore.getState().setReleasedGroups(releasedGroups)

    // 4. Batches 업데이트 (레거시 호환)
    storeBatchesFromGroups(groupsWithDetails)

    // 5. Released groups의 School 데이터를 SchoolStore에 저장
    if (Array.isArray(releasedGroups)) {
      releasedGroups.forEach((group) => {
        const items = group.items || []
        items.forEach((item) => {
          if (item.school && item.tank) {
            const school = item.school
            const tank = item.tank

            const averageWeight = school.averageWeight ?? school.weightPerFish ?? null
            const count = school.count ?? null
            const totalBiomass = school.totalBiomass ?? (
              count != null && averageWeight != null ? count * averageWeight : null
            )
            const density = school.density ?? (
              tank.volume > 0 && totalBiomass != null ? totalBiomass / tank.volume : null
            )

            let moveDate: number | null = null
            if (school.startDate) {
              const date = new Date(school.startDate)
              const year = date.getFullYear()
              const month = String(date.getMonth() + 1).padStart(2, '0')
              const day = String(date.getDate()).padStart(2, '0')
              moveDate = parseInt(`${year}${month}${day}`)
            }

            useSchoolStore.getState().addSchool(school.id, {
              averageWeight,
              count,
              totalBiomass,
              density,
              feedingRate: school.feedingRate ?? null,
              moveDate,
              startDate: school.startDate,
            })
          }
        })
      })
    }

    // 6. Grouped + Released tanks를 tanks store에 병합
    const tanksRecord: Record<string, Tank> = {}

    // Grouped tanks from groupsWithDetails
    if (Array.isArray(groupsWithDetails)) {
      groupsWithDetails.forEach((group) => {
        const items = group.items || []
        items.forEach((item) => {
          if (item.tank) {
            tanksRecord[item.tank.id] = {
              ...item.tank,
              schoolId: item.school?.id || null,
            }
          }
        })
      })
    }

    // Released (ungrouped with fish) tanks
    if (Array.isArray(releasedGroups)) {
      releasedGroups.forEach((group) => {
        const items = group.items || []
        items.forEach((item) => {
          if (item.tank) {
            tanksRecord[item.tank.id] = {
              ...item.tank,
              schoolId: item.school?.id || null,
            }
          }
        })
      })
    }

    // Empty tanks (no schoolId)
    emptyTanks.forEach((tank) => {
      tanksRecord[tank.id] = {
        ...tank,
        schoolId: null,
      }
    })

    useTankStore.getState().setTanks(tanksRecord)
  } catch (error) {
    console.error('Failed to reload groups with details:', error)
  }
}
